import React, { useState, useEffect } from 'react';
import { getNotasBySpanId } from '../services/merk_services';
import { getAllBeoordelaars } from '../services/beoordelaar_services';
import { thStyle, tdStyle, formatPunt, shade } from './span_vergelyking';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// What each beoordelaar gave one team: one row per beoordelaar with their punt for every
// kriteria, their total and their nota. The punte come from the round data the page has
// already loaded, so the only request here is one for the team's notas.
function SpanScoresBreakdown({ spanId, spanNaam, punte, merkblads, refreshKey = 0 }) {
  const [notas, setNotas] = useState([]);
  const [error, setError] = useState(null);
  const [beoordelaars, setBeoordelaars] = useState({}); // username -> { volle_naam, organisasie }

  useEffect(() => {
    let cancelled = false;
    getNotasBySpanId(spanId)
      .then(data => {
        if (!cancelled) {
          setNotas(data);
          setError(null);
        }
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      });
    return () => { cancelled = true; };
  }, [spanId, refreshKey]);

  // The beoordelaars' details (full name and organisation), so the table shows who scored
  useEffect(() => {
    let cancelled = false;
    getAllBeoordelaars()
      .then(data => {
        if (cancelled) return;
        const map = {};
        (data || []).forEach(b => { map[b.username] = b; });
        setBeoordelaars(map);
      })
      .catch(() => {}); // the table still works without the details
    return () => { cancelled = true; };
  }, []);

  // One row per beoordelaar who scored the team in this round, with their notas.
  // (Notas aren't per round, so notas from beoordelaars who only scored the team in
  // another round - e.g. rondte 1 when viewing the final - aren't shown here.)
  const rowsByBeoordelaar = {};
  const rowFor = (beoordelaar) => {
    const key = beoordelaar || 'Onbekend';
    if (!rowsByBeoordelaar[key]) rowsByBeoordelaar[key] = { beoordelaar: key, punte: {}, notas: [] };
    return rowsByBeoordelaar[key];
  };
  punte.forEach(punt => { rowFor(punt.beoordelaar).punte[punt.kriteria_id] = punt.punt; });
  notas.forEach(nota => {
    const row = rowsByBeoordelaar[nota.beoordelaar || 'Onbekend'];
    if (row) row.notas.push(nota.nota);
  });
  const rows = Object.values(rowsByBeoordelaar).sort((a, b) => a.beoordelaar.localeCompare(b.beoordelaar));
  const scoredRows = rows.filter(row => Object.keys(row.punte).length > 0);

  // Total of the kriteria that were scored, out of the maximum for those kriteria
  const totalFor = (puntePerKriteria) => {
    let total = 0;
    let max = 0;
    merkblads.forEach(merkblad => {
      const punt = puntePerKriteria[merkblad.kriteria_id];
      if (punt !== undefined) {
        total += punt;
        max += merkblad.totaal || 0;
      }
    });
    return { total, max };
  };

  // Average per kriteria over the beoordelaars who scored it
  const averages = {};
  merkblads.forEach(merkblad => {
    const values = scoredRows
      .map(row => row.punte[merkblad.kriteria_id])
      .filter(value => value !== undefined);
    if (values.length > 0) {
      averages[merkblad.kriteria_id] = values.reduce((sum, value) => sum + value, 0) / values.length;
    }
  });
  const averageTotal = totalFor(averages);

  // Open a printable, anonymous report for this team: beoordelaar names are replaced by
  // "Beoordelaar 1" (and so on) in a shuffled order, so the numbering can't be traced back.
  const printAnoniem = () => {
    const nameMap = {};
    [...rows.map(row => row.beoordelaar)]
      .sort(() => Math.random() - 0.5)
      .forEach((naam, index) => { nameMap[naam] = `Beoordelaar ${index + 1}`; });
    const anon = (naam) => nameMap[naam] || 'Onbekend';

    const kriteriaKoppe = merkblads
      .map(m => `<th>${escapeHtml(m.kriteria_beskrywing)}<br><small>/ ${m.totaal}</small></th>`)
      .join('');

    const liggame = rows.map(row => {
      const { total, max } = totalFor(row.punte);
      const selle = merkblads.map(m => {
        const punt = row.punte[m.kriteria_id];
        return punt === undefined
          ? '<td class="leeg">-</td>'
          : `<td>${punt} / ${m.totaal}</td>`;
      }).join('');
      return `<tr>
        <td class="naam">${escapeHtml(anon(row.beoordelaar))}</td>
        ${selle}
        <td class="totaal">${max > 0 ? `${total} / ${max}` : '-'}</td>
        <td class="nota">${escapeHtml(row.notas.join('\n\n')) || ''}</td>
      </tr>`;
    }).join('');

    const gemiddeldSel = merkblads.map(m => {
      const gem = averages[m.kriteria_id];
      return `<td>${gem === undefined ? '-' : formatPunt(gem)}</td>`;
    }).join('');

    const notasLys = rows
      .filter(row => row.notas.length > 0)
      .map(row => `<li><strong>${escapeHtml(anon(row.beoordelaar))}:</strong> ${escapeHtml(row.notas.join('\n\n'))}</li>`)
      .join('');

    const datum = new Date().toLocaleDateString('af-ZA', { year: 'numeric', month: 'long', day: 'numeric' });
    const html = `<!DOCTYPE html>
<html lang="af"><head><meta charset="utf-8">
<title>Anonieme beoordelingsverslag - ${escapeHtml(spanNaam)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #222; margin: 28px; }
  h1 { color: #0e1e3b; margin: 0 0 4px 0; }
  .datum { color: #666; margin: 0 0 20px 0; }
  table { border-collapse: collapse; width: 100%; font-size: 12px; margin-bottom: 20px; }
  th, td { border: 1px solid #ccc; padding: 5px 7px; text-align: center; vertical-align: top; }
  th { background: #f0f0f7; color: #0e1e3b; }
  td.naam, td.nota { text-align: left; }
  td.nota { white-space: pre-wrap; max-width: 260px; }
  td.totaal { font-weight: bold; }
  td.leeg { color: #999; }
  tfoot td { font-weight: bold; background: #f7f7fa; }
  h2 { color: #0e1e3b; margin: 20px 0 8px 0; }
  ul.notas { padding-left: 18px; }
  ul.notas li { margin-bottom: 8px; white-space: pre-wrap; }
  footer { margin-top: 24px; color: #666; font-size: 11px; border-top: 1px solid #ddd; padding-top: 8px; }
  @media print { body { margin: 12mm; } }
</style></head>
<body>
  <h1>Anonieme beoordelingsverslag</h1>
  <p class="datum">Span: <strong>${escapeHtml(spanNaam || `Span ID: ${spanId}`)}</strong> &middot; ${escapeHtml(datum)}</p>
  <table>
    <thead><tr><th>Beoordelaar</th>${kriteriaKoppe}<th>Totaal</th><th>Nota</th></tr></thead>
    <tbody>${liggame}</tbody>
    ${scoredRows.length > 1 ? `<tfoot><tr><td>Gemiddeld</td>${gemiddeldSel}<td>${averageTotal.max > 0 ? `${formatPunt(averageTotal.total)} / ${averageTotal.max}` : '-'}</td><td></td></tr></tfoot>` : ''}
  </table>
  ${notasLys ? `<h2>Opmerkings</h2><ul class="notas">${notasLys}</ul>` : ''}
  <footer>
    Die name van die beoordelaars is weggelaat; die nommers is in 'n lukrake volgorde en stem nie ooreen met enige amptelike lys nie.
  </footer>
</body></html>`;

    const venster = window.open('', '_blank');
    if (!venster) {
      setError('Kon nie die verslag oopmaak nie (dalk is die opwipblokkeerder aan).');
      return;
    }
    venster.document.write(html);
    venster.document.close();
    venster.focus();
    venster.print();
  };

  // Small inline SVG chart component
  const DistributionChart = ({ dataByKriteria }) => {
    // dataByKriteria: { kriteriaLabel: [percentNumber, ...] }
    const kriteriaKeys = Object.keys(dataByKriteria);
    if (kriteriaKeys.length === 0) return null;

    const width = Math.max(1200, kriteriaKeys.length * 140);
    const height = 480;
    const padding = { top: 20, right: 20, bottom: 40, left: 40 };
    const innerWidth = width - padding.left - padding.right;
    const innerHeight = height - padding.top - padding.bottom;

    const columnWidth = innerWidth / kriteriaKeys.length;

    return (
      <div style={{ marginBottom: 10, overflowX: 'auto' }}>
        <svg width={width} height={height} style={{ background: '#fff' }}>
          <g transform={`translate(${padding.left},${padding.top})`}>
            {/* y axis lines and labels (0%..100% every 25%) */}
            {[0,10,20,30,40,50,60,70,80,90,100].map((v) => {
              const y = innerHeight - (v/100) * innerHeight;
              return (
                <g key={v}>
                  <line x1={0} y1={y} x2={innerWidth} y2={y} stroke="#eee" />
                  <text x={-10} y={y+4} fontSize={10} textAnchor="end" fill="#666">{v}%</text>
                </g>
              );
            })}

            {/* columns and dots */}
            {kriteriaKeys.map((k, i) => {
              const percentages = dataByKriteria[k] || [];
              const colX = i * columnWidth + columnWidth/2;
              return (
                <g key={k}>
                  {/* kriteria label */}
                  <text x={colX} y={innerHeight + 18} fontSize={12} textAnchor="middle" fill="#333">
                    {k.length > 20 ? k.substring(0,17) + '...' : k}
                  </text>

                  {/* dots for each judge */}
                  {percentages.map((p, idx) => {
                    const y = innerHeight - (p/100) * innerHeight;
                    // spread dots slightly horizontally to avoid exact overlap
                    const jitter = (idx - (percentages.length-1)/2) * 4;
                    // alternate between teal and burgundy colors
                    const dotColor = i % 2 === 0 ? "#2b7a78" : "#7a2b2e";
                    return (
                      <circle key={idx} cx={colX + jitter} cy={y} r={5} fill={dotColor} stroke="#fff" strokeWidth={1} />
                    );
                  })}
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    );
  };

  // Chart data: every beoordelaar's punt per kriteria, as a percentage of the maximum
  const chartData = {};
  merkblads.forEach(merkblad => {
    chartData[merkblad.kriteria_beskrywing || `Kriteria ${merkblad.kriteria_id}`] = scoredRows
      .map(row => row.punte[merkblad.kriteria_id])
      .filter(punt => punt !== undefined)
      .map(punt => (merkblad.totaal > 0 ? Math.round((punt / merkblad.totaal) * 100) : 0));
  });

  const footerCell = { ...tdStyle, fontWeight: 'bold', borderTop: '2px solid #0e1e3b' };

  return (
    <div className="span-scores-breakdown" style={{ marginTop: '0.9rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: '0 0 0.5rem 0' }}>Span Punte Besonderhede vir {spanNaam || `Span ID: ${spanId}`}</h2>
        {rows.length > 0 && (
          <button
            type="button"
            onClick={printAnoniem}
            style={{
              padding: '8px 14px',
              backgroundColor: '#0e1e3b',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Anonieme PDF-verslag
          </button>
        )}
      </div>
      {error && <p style={{ color: '#a32020' }}>Kon nie notas laai nie: {error}</p>}
      {scoredRows.length > 0 && <DistributionChart dataByKriteria={chartData} />}

      {rows.length === 0 ? (
        <p>Geen punte vir hierdie span in hierdie rondte nie.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', backgroundColor: 'white', fontSize: '0.9rem' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left' }}>Beoordelaar</th>
                <th style={{ ...thStyle, textAlign: 'left', minWidth: '120px' }}>Besonderhede</th>
                {merkblads.map(merkblad => (
                  <th
                    key={merkblad.merkblad_id}
                    style={{ ...thStyle, minWidth: '56px', maxWidth: '130px' }}
                    title={merkblad.kriteria_beskrywing}
                  >
                    {merkblad.kriteria_beskrywing}
                    <div style={{ fontWeight: 'normal', color: '#666' }}>/ {merkblad.totaal}</div>
                  </th>
                ))}
                <th style={thStyle}>Totaal</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Nota</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const { total, max } = totalFor(row.punte);
                return (
                  <tr key={row.beoordelaar}>
                    <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 'bold', color: '#0e1e3b' }}>
                      {row.beoordelaar}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'left' }}>
                      {(() => {
                        const info = beoordelaars[row.beoordelaar];
                        if (!info) return <span style={{ color: '#999' }}>-</span>;
                        return (
                          <div style={{ color: '#333', fontSize: '0.85rem' }}>
                            {info.volle_naam && <div>{info.volle_naam}</div>}
                            {info.organisasie && <div style={{ color: '#666' }}>{info.organisasie}</div>}
                            {info.epos && <div style={{ color: '#888' }}>{info.epos}</div>}
                          </div>
                        );
                      })()}
                    </td>
                    {merkblads.map(merkblad => {
                      const punt = row.punte[merkblad.kriteria_id];
                      if (punt === undefined) {
                        return <td key={merkblad.merkblad_id} style={{ ...tdStyle, color: '#999' }}>-</td>;
                      }
                      return (
                        <td
                          key={merkblad.merkblad_id}
                          title={`${punt} / ${merkblad.totaal}`}
                          style={{ ...tdStyle, backgroundColor: shade(merkblad.totaal ? punt / merkblad.totaal : 0) }}
                        >
                          {punt}
                        </td>
                      );
                    })}
                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>
                      {max > 0 ? `${total} / ${max}` : '-'}
                    </td>
                    <td style={{
                      ...tdStyle,
                      textAlign: 'left',
                      whiteSpace: 'pre-wrap',
                      color: '#333',
                      minWidth: '240px'
                    }}>
                      {row.notas.join('\n\n')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {scoredRows.length > 1 && (
              <tfoot>
                <tr>
                  <td style={{ ...footerCell, textAlign: 'left' }}>Gemiddeld</td>
                  <td style={footerCell} />
                  {merkblads.map(merkblad => {
                    const average = averages[merkblad.kriteria_id];
                    return (
                      <td key={merkblad.merkblad_id} style={footerCell}>
                        {average === undefined ? '-' : formatPunt(average)}
                      </td>
                    );
                  })}
                  <td style={footerCell}>
                    {averageTotal.max > 0 ? `${formatPunt(averageTotal.total)} / ${averageTotal.max}` : '-'}
                  </td>
                  <td style={footerCell} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

export default SpanScoresBreakdown;
